import axios from "axios";
import { normalizeChatMessagePageLimit } from "../constants/chat";
import { resolveGatewayBackedServiceBaseUrl } from "./serviceBaseUrl";

const GROUPS_SERVICE_BASE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_API_URL);
const CHAT_SERVICE_BASE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_CHAT_SERVICE_URL);
const MEDIA_SERVICE_BASE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_MEDIA_SERVICE_URL);

const GROUPS_API_BASE = `${GROUPS_SERVICE_BASE_URL}/api/groups`;
const CHAT_API_BASE = `${CHAT_SERVICE_BASE_URL}/api/chat`;
const GROUP_MEDIA_API_BASE = `${MEDIA_SERVICE_BASE_URL}/api/media/groups`;

export const getGroups = async () => {
    const response = await axios.get(GROUPS_API_BASE, { withCredentials: true });
    return response.data;
};

export const getGroupDetails = async (groupId) => {
    const response = await axios.get(`${GROUPS_API_BASE}/${groupId}`, { withCredentials: true });
    return response.data;
};

export const createGroup = async (payload) => {
    const response = await axios.post(GROUPS_API_BASE, payload, { withCredentials: true });
    return response.data;
};

export const updateGroup = async (groupId, payload) => {
    const response = await axios.patch(`${GROUPS_API_BASE}/${groupId}`, payload, { withCredentials: true });
    return response.data;
};

export const deleteGroup = async (groupId) => {
    await axios.delete(`${GROUPS_API_BASE}/${groupId}`, { withCredentials: true });
};

export const getGroupChannels = async (groupId) => {
    const response = await axios.get(`${GROUPS_API_BASE}/${groupId}/channels`, { withCredentials: true });
    return response.data;
};

export const getGroupChannelDetails = async (groupId, channelId) => {
    const response = await axios.get(`${GROUPS_API_BASE}/${groupId}/channels/${channelId}`, {
        withCredentials: true,
    });
    return response.data;
};

export const createGroupChannel = async (groupId, payload) => {
    const response = await axios.post(`${GROUPS_API_BASE}/${groupId}/channels`, payload, { withCredentials: true });
    return response.data;
};

export const updateGroupChannel = async (groupId, channelId, payload) => {
    const response = await axios.patch(`${GROUPS_API_BASE}/${groupId}/channels/${channelId}`, payload, { withCredentials: true });
    return response.data;
};

export const deleteGroupChannel = async (groupId, channelId) => {
    await axios.delete(`${GROUPS_API_BASE}/${groupId}/channels/${channelId}`, { withCredentials: true });
};

export const getGroupCalendars = async (groupId) => {
    const response = await axios.get(`${GROUPS_API_BASE}/${groupId}/calendars`, { withCredentials: true });
    return response.data;
};

export const getGroupEvents = async (groupId, calendarId, params = {}) => {
    const response = await axios.get(`${GROUPS_API_BASE}/${groupId}/calendars/${calendarId}/events`, {
        withCredentials: true,
        params,
    });
    return response.data;
};

export const getGroupBoard = async (groupId, boardId) => {
    const response = await axios.get(`${GROUPS_API_BASE}/${groupId}/boards/${boardId}`, { withCredentials: true });
    return response.data;
};

export const createGroupBoard = async (groupId, payload) => {
    const response = await axios.post(`${GROUPS_API_BASE}/${groupId}/boards`, payload, { withCredentials: true });
    return response.data;
};

export const createGroupEvent = async (groupId, calendarId, payload) => {
    const response = await axios.post(
        `${GROUPS_API_BASE}/${groupId}/calendars/${calendarId}/events`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const updateGroupEvent = async (groupId, calendarId, eventId, payload) => {
    const response = await axios.patch(
        `${GROUPS_API_BASE}/${groupId}/calendars/${calendarId}/events/${eventId}`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const deleteGroupEvent = async (groupId, calendarId, eventId) => {
    await axios.delete(`${GROUPS_API_BASE}/${groupId}/calendars/${calendarId}/events/${eventId}`, {
        withCredentials: true,
    });
};

export const rsvpGroupEvent = async (groupId, calendarId, eventId, payload) => {
    const response = await axios.post(
        `${GROUPS_API_BASE}/${groupId}/calendars/${calendarId}/events/${eventId}/rsvp`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const uploadGroupFile = async (groupId, channelId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axios.post(
        `${GROUP_MEDIA_API_BASE}/${groupId}/channels/${channelId}/upload`,
        formData,
        {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
        }
    );
    return response.data;
};

export const listGroupFiles = async (groupId, channelId) => {
    const response = await axios.get(
        `${GROUP_MEDIA_API_BASE}/${groupId}/channels/${channelId}/items`,
        { withCredentials: true }
    );
    return response.data;
};

export const downloadGroupFileUrl = (groupId, objectKey) => (
    `${GROUP_MEDIA_API_BASE}/${groupId}/download?objectKey=${encodeURIComponent(objectKey)}`
);

export const deleteGroupFile = async (groupId, objectKey) => {
    await axios.delete(
        `${GROUP_MEDIA_API_BASE}/${groupId}/items`,
        {
            withCredentials: true,
            params: { objectKey },
        }
    );
};

export const createGroupCard = async (groupId, boardId, payload) => {
    const response = await axios.post(
        `${GROUPS_API_BASE}/${groupId}/boards/${boardId}/cards`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const createGroupTaskList = async (groupId, boardId, payload) => {
    const response = await axios.post(
        `${GROUPS_API_BASE}/${groupId}/boards/${boardId}/lists`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const updateGroupTaskList = async (groupId, boardId, listId, payload) => {
    const response = await axios.patch(
        `${GROUPS_API_BASE}/${groupId}/boards/${boardId}/lists/${listId}`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const deleteGroupTaskList = async (groupId, boardId, listId) => {
    await axios.delete(`${GROUPS_API_BASE}/${groupId}/boards/${boardId}/lists/${listId}`, {
        withCredentials: true,
    });
};

export const getGroupChannelMessages = async (groupId, channelId) => {
    const response = await axios.get(`${CHAT_API_BASE}/groups/${groupId}/channels/${channelId}/messages`, { withCredentials: true });
    return response.data;
};

export const getGroupChannelMessagesPage = async (groupId, channelId, params = {}) => {
    const query = {
        limit: normalizeChatMessagePageLimit(params.limit),
    };
    if (params.cursor) {
        query.cursor = params.cursor;
    }
    const config = { withCredentials: true, params: query };
    const response = await axios.get(`${CHAT_API_BASE}/groups/${groupId}/channels/${channelId}/messages/page`, config);
    return response.data;
};

export const sendGroupChannelMessage = async (groupId, channelId, payload) => {
    const response = await axios.post(`${CHAT_API_BASE}/groups/${groupId}/channels/${channelId}/messages`, payload, { withCredentials: true });
    return response.data;
};

export const updateGroupChannelPresence = async (groupId, channelId, state) => {
    const response = await axios.post(
        `${CHAT_API_BASE}/groups/${groupId}/channels/${channelId}/presence`,
        { state },
        { withCredentials: true }
    );
    return response.data;
};

export const getGroupChannelPresence = async (groupId, channelId) => {
    const response = await axios.get(`${CHAT_API_BASE}/groups/${groupId}/channels/${channelId}/presence`, { withCredentials: true });
    return response.data;
};

export const getGroupChannelUnread = async (groupId, channelId) => {
    const response = await axios.get(`${CHAT_API_BASE}/groups/${groupId}/channels/${channelId}/unread`, { withCredentials: true });
    return response.data;
};

export const getGroupChannelUnreadSnapshot = async (groupId, channelId) => {
    const response = await axios.get(`${CHAT_API_BASE}/groups/${groupId}/channels/${channelId}/unread-snapshot`, { withCredentials: true });
    return response.data;
};

export const markGroupChannelRead = async (channelId, userId) => {
    await axios.post(`${CHAT_API_BASE}/${channelId}/mark-as-read/${userId}`, null, { withCredentials: true });
};

export const reorderGroupBoardLists = async (groupId, boardId, payload) => {
    const response = await axios.patch(
        `${GROUPS_API_BASE}/${groupId}/boards/${boardId}/lists/reorder`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const updateGroupCard = async (groupId, boardId, cardId, payload) => {
    const response = await axios.patch(
        `${GROUPS_API_BASE}/${groupId}/boards/${boardId}/cards/${cardId}`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const moveGroupBoardCard = async (groupId, boardId, cardId, payload) => {
    const response = await axios.post(
        `${GROUPS_API_BASE}/${groupId}/boards/${boardId}/cards/${cardId}/move`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const listGroupCardComments = async (groupId, boardId, cardId, params = {}) => {
    const query = {};
    if (typeof params.page === "number") {
        query.page = params.page;
    }
    if (typeof params.size === "number") {
        query.size = params.size;
    }
    const config = { withCredentials: true };
    if (Object.keys(query).length) {
        config.params = query;
    }
    const response = await axios.get(
        `${GROUPS_API_BASE}/${groupId}/boards/${boardId}/cards/${cardId}/comments`,
        config
    );
    return response.data;
};

export const addGroupCardComment = async (groupId, boardId, cardId, payload) => {
    const response = await axios.post(
        `${GROUPS_API_BASE}/${groupId}/boards/${boardId}/cards/${cardId}/comments`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const updateGroupCardComment = async (groupId, boardId, cardId, commentId, payload) => {
    const response = await axios.patch(
        `${GROUPS_API_BASE}/${groupId}/boards/${boardId}/cards/${cardId}/comments/${commentId}`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const deleteGroupCardComment = async (groupId, boardId, cardId, commentId) => {
    await axios.delete(
        `${GROUPS_API_BASE}/${groupId}/boards/${boardId}/cards/${cardId}/comments/${commentId}`,
        { withCredentials: true }
    );
};

export const deleteGroupCard = async (groupId, boardId, cardId) => {
    await axios.delete(`${GROUPS_API_BASE}/${groupId}/boards/${boardId}/cards/${cardId}`, { withCredentials: true });
};

export const updateGroupChecklistItem = async (groupId, boardId, itemId, payload) => {
    const response = await axios.patch(
        `${GROUPS_API_BASE}/${groupId}/boards/${boardId}/checklist-items/${itemId}`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const listGroupMembers = async (groupId) => {
    const response = await axios.get(`${GROUPS_API_BASE}/${groupId}/members`, { withCredentials: true });
    return response.data;
};

export const updateGroupMember = async (groupId, membershipId, payload) => {
    const response = await axios.patch(
        `${GROUPS_API_BASE}/${groupId}/members/${membershipId}`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const removeGroupMember = async (groupId, membershipId) => {
    await axios.delete(`${GROUPS_API_BASE}/${groupId}/members/${membershipId}`, { withCredentials: true });
};

export const inviteGroupMembers = async (groupId, payload) => {
    const response = await axios.post(`${GROUPS_API_BASE}/${groupId}/invites`, payload, { withCredentials: true });
    return response.data;
};

export const getGroupInvites = async (groupId) => {
    const response = await axios.get(`${GROUPS_API_BASE}/${groupId}/invites`, { withCredentials: true });
    return response.data;
};

export const revokeGroupInvite = async (groupId, inviteId) => {
    await axios.delete(`${GROUPS_API_BASE}/${groupId}/invites/${inviteId}`, { withCredentials: true });
};

export const getPublicGroupInvite = async (token) => {
    const response = await axios.get(`${GROUPS_API_BASE}/public/invites/${token}`, { withCredentials: true });
    return response.data;
};

export const acceptGroupInvite = async (token, payload = {}) => {
    const response = await axios.post(
        `${GROUPS_API_BASE}/public/invites/${token}/accept`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const getGroupFriendCandidates = async (groupId, params = {}) => {
    const response = await axios.get(`${GROUPS_API_BASE}/${groupId}/members/friends`, {
        withCredentials: true,
        params,
    });
    return response.data;
};

export const addGroupFriends = async (groupId, payload) => {
    const response = await axios.post(
        `${GROUPS_API_BASE}/${groupId}/members/friends`,
        payload,
        { withCredentials: true }
    );
    return response.data;
};

export const getPublicGroups = async (params = {}) => {
    const response = await axios.get(`${GROUPS_API_BASE}/public`, {
        params,
        withCredentials: true,
    });
    return response.data;
};

export const getPublicGroup = async (slugOrId) => {
    const response = await axios.get(`${GROUPS_API_BASE}/public/${slugOrId}`, { withCredentials: true });
    return response.data;
};

export const joinPublicGroup = async (groupId) => {
    const response = await axios.post(`${GROUPS_API_BASE}/public/${groupId}/join`, null, { withCredentials: true });
    return response.data;
};

export const leaveGroup = async (groupId) => {
    await axios.post(`${GROUPS_API_BASE}/${groupId}/members/leave`, null, { withCredentials: true });
};
